import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, Trophy, Users, Calendar, Crown, Smartphone, Monitor,
  CalendarDays, Layers,
} from "lucide-react";

/**
 * World Cup Immersion mode (iter 62).
 *
 * One page with three sub-tabs:
 *  - Today: your assigned team + opponent + scoring form for both legs.
 *  - Leaderboard: live ranks, points-to-qualify indicator.
 *  - Bracket: empty placeholders during group stage, populated post-cut.
 */
export default function WCImmersionV2() {
  const { pool: rawPool } = useParams();
  const pool = (rawPool === "console") ? "console" : "mobile";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("today");
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wci/state?pool=${pool}`);
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [pool]);
  useEffect(() => { load(); }, [load]);

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070710]">
        <Loader2 className="w-6 h-6 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  const t = state.tournament;
  const PoolIcon = pool === "console" ? Monitor : Smartphone;

  return (
    <div className="min-h-screen bg-[#070710] text-white pb-32" data-testid="v2-wci-root">
      {/* Hero with flag-strip backdrop */}
      <div className="relative overflow-hidden">
        <FlagStrip />
        <div className="relative bg-gradient-to-br from-[#1E3A8A]/95 via-[#7C3AED]/90 to-[#FFD700]/30 px-4 pt-6 pb-5">
          <button
            type="button"
            onClick={() => navigate("/v2/explore")}
            data-testid="v2-wci-back"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/80 hover:text-white mb-3"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Explore
          </button>
          <div className="flex items-center gap-2 mb-1">
            <PoolIcon className="w-3.5 h-3.5 text-white/70" />
            <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/70">{pool}</span>
            <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/40">·</span>
            <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#FFD700]">2026 Group Stage</span>
          </div>
          <h1 className="text-2xl font-extrabold text-white drop-shadow">World Cup · Immersion</h1>
          <p className="text-[12px] text-white/85 mt-1 leading-snug">
            48 nations · 12 groups · daily fixture sync · top 50% advance each stage.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/80">
            <span data-testid="v2-wci-status">Status: {t.status.replace(/_/g, " ")}</span>
            <span>·</span>
            <span data-testid="v2-wci-active">{state.total_active}/{state.total_joined} active</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 px-4 flex gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { id: "today",       label: "Today",       Icon: Calendar },
          { id: "calendar",    label: "Calendar",    Icon: CalendarDays },
          { id: "groups",      label: "Groups",      Icon: Layers },
          { id: "leaderboard", label: "Leaderboard", Icon: Trophy },
          { id: "bracket",     label: "Bracket",     Icon: Crown },
        ].map(({ id, label, Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              data-testid={`v2-wci-tab-${id}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-mono uppercase tracking-widest border-b-2 transition-colors ${
                active ? "border-[#FFD700] text-[#FFD700]" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              <Icon className="w-3 h-3" /> {label}
            </button>
          );
        })}
      </div>

      <div className="px-4 mt-4 space-y-4">
        {tab === "today"       && <TodayView state={state} pool={pool} onChanged={load} userId={user?.id} />}
        {tab === "calendar"    && <CalendarView pool={pool} />}
        {tab === "groups"      && <GroupsView pool={pool} />}
        {tab === "leaderboard" && <LeaderboardView pool={pool} userId={user?.id} />}
        {tab === "bracket"     && <BracketView pool={pool} />}
      </div>
    </div>
  );
}

/* ===================== FLAG STRIP (hero motif) ===================== */
function FlagStrip() {
  // Subtle marquee of nation flags behind the hero. Plays decoratively.
  const flags = [
    "🇲🇽","🇿🇦","🇰🇷","🇨🇿","🇨🇦","🇧🇦","🇶🇦","🇨🇭","🇧🇷","🇲🇦","🇭🇹","🏴󠁧󠁢󠁳󠁣󠁴󠁿",
    "🇺🇸","🇵🇾","🇦🇺","🇹🇷","🇩🇪","🇨🇼","🇨🇮","🇪🇨","🇳🇱","🇯🇵","🇸🇪","🇹🇳",
    "🇧🇪","🇪🇬","🇮🇷","🇳🇿","🇪🇸","🇨🇻","🇸🇦","🇺🇾","🇫🇷","🇸🇳","🇮🇶","🇳🇴",
    "🇦🇷","🇩🇿","🇦🇹","🇯🇴","🇵🇹","🇨🇩","🇺🇿","🇨🇴","🏴󠁧󠁢󠁥󠁮󠁧󠁿","🇭🇷","🇬🇭","🇵🇦",
  ];
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none opacity-25" data-testid="v2-wci-flag-strip">
      <div className="absolute top-2 left-0 right-0 flex gap-2 text-2xl whitespace-nowrap overflow-hidden">
        {flags.concat(flags).map((f, i) => <span key={i}>{f}</span>)}
      </div>
      <div className="absolute bottom-2 left-0 right-0 flex gap-2 text-2xl whitespace-nowrap overflow-hidden flex-row-reverse">
        {flags.concat(flags).map((f, i) => <span key={i}>{f}</span>)}
      </div>
    </div>
  );
}

/* ===================== TODAY ===================== */
function TodayView({ state, pool, onChanged, userId }) {
  const hype = state.today_hype;
  const p = state.today_pairing;
  const myA = state.my_assignment;
  return (
    <div className="space-y-4">
      {hype && <MatchOfTheDayCard hype={hype} />}
      {!state.has_matchday_today ? (
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-sm text-[#A1A1AA]" data-testid="v2-wci-no-match-today">
          <Calendar className="w-6 h-6 mx-auto mb-2 opacity-50" />
          No real-WC fixtures today. Rest up — your next matchday is on the schedule.
        </div>
      ) : state.draw_available ? (
        <DrawCard pool={pool} onDrawn={onChanged} />
      ) : p && p.bye ? (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/40 p-6 text-center text-sm text-amber-200" data-testid="v2-wci-bye">
          You drew a BYE today. No game, no points lost. Back tomorrow.
        </div>
      ) : p ? (
        <PairingCard p={p} userId={userId} onChanged={onChanged} />
      ) : myA ? (
        <WaitingForOpponent assignment={myA} pool={pool} onChanged={onChanged} />
      ) : null}
    </div>
  );
}

/* ----- DRAW your team ----- */
function DrawCard({ pool, onDrawn }) {
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);
  const handleDraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wci/draw", { pool });
      // Brief reveal animation before refreshing the parent state.
      setReveal(data.assignment);
      setTimeout(() => { onDrawn?.(); }, 1400);
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  if (reveal) {
    return (
      <div className="relative overflow-hidden rounded-3xl border-2 border-[#FFD700] bg-gradient-to-br from-[#1E3A8A] via-[#7C3AED] to-[#FFD700]/40 px-4 py-8 text-center shadow-[0_0_60px_-10px_rgba(255,215,0,0.6)]" data-testid="v2-wci-draw-reveal">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFD700]">// You drew</div>
        <div className="text-7xl mt-3 animate-bounce">{reveal.team.flag}</div>
        <div className="text-2xl font-extrabold text-white mt-2">{reveal.team.name}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mt-2">
          Fixture {reveal.fixture_code} · Playing {reveal.role.toUpperCase()}
        </div>
      </div>
    );
  }
  return (
    <div className="relative overflow-hidden rounded-3xl border-2 border-dashed border-[#FFD700]/50 bg-gradient-to-br from-[#1E3A8A]/40 via-[#7C3AED]/30 to-[#FFD700]/10 px-4 py-7 text-center" data-testid="v2-wci-draw-card">
      <div aria-hidden className="absolute -top-6 -right-6 text-[140px] opacity-10 select-none pointer-events-none">🎲</div>
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFD700]">// Step 1</div>
      <h2 className="text-xl font-extrabold text-white mt-1">Draw your nation</h2>
      <p className="text-[12px] text-white/80 mt-1.5 max-w-xs mx-auto leading-snug">
        One random team from today's 3 real-world fixtures. You'll be auto-matched 1v1 against whoever draws the opposing nation.
      </p>
      <button
        type="button"
        onClick={handleDraw}
        disabled={busy}
        data-testid="v2-wci-draw-btn"
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FFB000] text-[#0F172A] font-extrabold text-sm px-6 py-3 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="text-lg">🎲</span>}
        {busy ? "Drawing…" : "Draw a team"}
      </button>
    </div>
  );
}

/* ----- Waiting for opponent ----- */
function WaitingForOpponent({ assignment, pool, onChanged }) {
  // Lightweight poll to detect when an opponent draws.
  useEffect(() => {
    const id = setInterval(() => onChanged?.(), 6000);
    return () => clearInterval(id);
  }, [onChanged]);
  const myRoleLabel = (assignment.role || "").toUpperCase();
  return (
    <div className="rounded-3xl border border-[#FFD700]/40 bg-gradient-to-br from-[#12121C] to-[#1F1F2A] overflow-hidden" data-testid="v2-wci-waiting">
      <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#FFD700]/20">
        Waiting for opponent · Fixture {assignment.fixture_code}
      </div>
      <div className="p-6 text-center">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] mb-2">// Your team</div>
        <div className="text-6xl">{assignment.team.flag}</div>
        <div className="text-lg font-extrabold text-white mt-1">{assignment.team.name}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mt-1">Playing {myRoleLabel}</div>
        <div className="mt-5 flex items-center justify-center gap-3 text-white/40">
          <span className="text-3xl">🆚</span>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-[11px] font-mono uppercase tracking-widest">finding opponent</span>
        </div>
        <p className="text-[11px] text-[#A1A1AA] mt-5 max-w-xs mx-auto">
          As soon as another player draws the opposing nation in this fixture, you'll be paired automatically.
        </p>
      </div>
    </div>
  );
}

/* ----- Match of the Day hype card ----- */
function MatchOfTheDayCard({ hype }) {
  const [countdown, setCountdown] = useState(() => _computeCountdown(hype.kickoff_utc));
  useEffect(() => {
    setCountdown(_computeCountdown(hype.kickoff_utc));
    const id = setInterval(() => setCountdown(_computeCountdown(hype.kickoff_utc)), 1000);
    return () => clearInterval(id);
  }, [hype.kickoff_utc]);
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-[#FFD700]/40 bg-gradient-to-br from-[#1E3A8A] via-[#7C3AED]/70 to-[#FFD700]/30 shadow-[0_0_50px_-12px_rgba(255,215,0,0.55)]"
      data-testid="v2-wci-motd"
    >
      <div aria-hidden className="absolute -top-12 -right-10 text-[160px] opacity-15 select-none pointer-events-none">🏆</div>
      <div className="relative px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#FFD700]" data-testid="v2-wci-motd-kicker">
            Match of the Day
          </span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-white/70">
            G{hype.group} · {hype.fixture_code}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 text-center" data-testid="v2-wci-motd-home">
            <div className="text-5xl drop-shadow-lg">{hype.home.flag}</div>
            <div className="text-[12px] font-extrabold text-white mt-1 leading-tight">{hype.home.name}</div>
          </div>
          <div className="text-center shrink-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">vs</div>
            <div className="mt-1 text-[10px] font-bold tabular-nums text-[#FFD700]" data-testid="v2-wci-motd-countdown">
              {countdown.label}
            </div>
            {countdown.kickoff && (
              <div className="text-[9px] font-mono text-white/60 mt-0.5">
                {countdown.kickoff} UTC
              </div>
            )}
          </div>
          <div className="flex-1 text-center" data-testid="v2-wci-motd-away">
            <div className="text-5xl drop-shadow-lg">{hype.away.flag}</div>
            <div className="text-[12px] font-extrabold text-white mt-1 leading-tight">{hype.away.name}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function _computeCountdown(kickoffIso) {
  if (!kickoffIso) return { label: "—", kickoff: null };
  const target = Date.parse(kickoffIso);
  if (Number.isNaN(target)) return { label: "—", kickoff: null };
  const now = Date.now();
  const diff = target - now;
  const kt = kickoffIso.slice(11, 16);
  if (diff <= 0) {
    // Within the 2-hour match window, show LIVE; afterward FULL TIME.
    const elapsedMin = Math.floor(-diff / 60000);
    if (elapsedMin < 120) return { label: "● LIVE", kickoff: kt };
    return { label: "FULL TIME", kickoff: kt };
  }
  const totalSec = Math.floor(diff / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return { label: `${pad(h)}:${pad(m)}:${pad(s)}`, kickoff: kt };
}

function PairingCard({ p, userId, onChanged }) {
  const iAmHomeLeg1 = p.home_user === userId;
  const opponentId = iAmHomeLeg1 ? p.away_user : p.home_user;
  const opponentTeam = iAmHomeLeg1 ? p.away_team : p.home_team;
  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#12121C] to-[#1F1F2A] border border-[#FFD700]/30 overflow-hidden" data-testid={`v2-wci-pairing-${p.id}`}>
      <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#FFD700]/20 flex items-center justify-between">
        <span>{p.matchday_id} · {p.stage.toUpperCase()} · {p.fixture_code}</span>
        <span>Kickoff: {(p.kickoff_utc || "").slice(11, 16)} UTC</span>
      </div>
      <div className="p-4">
        <TeamHeader home={p.home_team} away={p.away_team} />
        <Link
          to={`/v2/chat/${opponentId}`}
          data-testid={`v2-wci-chat-${p.id}`}
          className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#A5B4FC] py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-[#7C3AED]/25 transition-colors"
        >
          💬 Chat with {opponentTeam.flag} opponent
        </Link>
        <LegRow legNum={1} pairing={p} iAmHome={iAmHomeLeg1} userId={userId} onChanged={onChanged} />
        <LegRow legNum={2} pairing={p} iAmHome={!iAmHomeLeg1} userId={userId} onChanged={onChanged} />
      </div>
    </div>
  );
}

function TeamHeader({ home, away }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="text-center flex-1">
        <div className="text-3xl">{home.flag}</div>
        <div className="text-xs font-bold text-white mt-1">{home.name}</div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 px-2">vs</div>
      <div className="text-center flex-1">
        <div className="text-3xl">{away.flag}</div>
        <div className="text-xs font-bold text-white mt-1">{away.name}</div>
      </div>
    </div>
  );
}

function LegRow({ legNum, pairing, iAmHome, userId, onChanged }) {
  const sk = `leg${legNum}_status`;
  const homeKey = `leg${legNum}_home_score`;
  const awayKey = `leg${legNum}_away_score`;
  const status = pairing[sk];
  const homeScore = pairing[homeKey];
  const awayScore = pairing[awayKey];
  const subs = pairing[`leg${legNum}_subs`] || {};
  const mySubmitted = !!subs[userId];

  const confirmed = status === "confirmed";
  const disputed  = status === "disputed";

  const hostUser = pairing[`leg${legNum}_host_user`];
  const iAmHost = hostUser === userId;
  const roomId = pairing[`leg${legNum}_room_id`];

  return (
    <div className="border-t border-white/10 py-3" data-testid={`v2-wci-leg-${pairing.id}-${legNum}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC]">
          Leg {legNum} · You play {iAmHome ? "HOME" : "AWAY"} · {iAmHost ? "HOST" : "GUEST"}
        </span>
        <span
          className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
            confirmed ? "bg-emerald-500/20 text-emerald-300" :
            disputed  ? "bg-rose-500/20 text-rose-300" :
                        "bg-amber-500/20 text-amber-300"
          }`}
        >
          {status}
        </span>
      </div>
      {!confirmed && !disputed && (
        <RoomIdRow pairing={pairing} legNum={legNum} iAmHost={iAmHost} roomId={roomId} onChanged={onChanged} />
      )}
      {confirmed ? (
        <div className="text-center text-2xl font-bold text-white tabular-nums">
          {homeScore} <span className="text-white/40 text-sm mx-2">—</span> {awayScore}
        </div>
      ) : disputed ? (
        <div className="text-center text-xs text-rose-300">Scores don't match. Admin will resolve.</div>
      ) : mySubmitted ? (
        <div className="text-center text-xs text-amber-300">Waiting on the other side to submit.</div>
      ) : (
        <SubmitForm pairing={pairing} legNum={legNum} iAmHome={iAmHome} onChanged={onChanged} />
      )}
    </div>
  );
}

function RoomIdRow({ pairing, legNum, iAmHost, roomId, onChanged }) {
  const [draft, setDraft] = useState(roomId || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(roomId || ""); }, [roomId]);
  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) { toast.error("Room ID can't be empty"); return; }
    setBusy(true);
    try {
      await api.post("/wci/room", { pairing_id: pairing.id, leg: legNum, room_id: trimmed });
      toast.success("Room ID shared.");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (iAmHost) {
    return (
      <div className="mb-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-2.5" data-testid={`v2-wci-roomid-host-${pairing.id}-${legNum}`}>
        <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300 mb-1.5">
          You are HOST — create a room in-game and share the ID
        </div>
        <div className="flex items-center gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. ROOM-123456"
            maxLength={64}
            style={{ fontSize: "16px" }}
            data-testid={`v2-wci-roomid-input-${pairing.id}-${legNum}`}
            className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-400"
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid={`v2-wci-roomid-save-${pairing.id}-${legNum}`}
            className="rounded-lg bg-emerald-500 text-[#04140A] font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : roomId ? "Update" : "Share"}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="mb-3 rounded-xl bg-[#7C3AED]/10 border border-[#7C3AED]/30 p-2.5" data-testid={`v2-wci-roomid-guest-${pairing.id}-${legNum}`}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#A5B4FC] mb-1">
        You are GUEST — join the host's room
      </div>
      {roomId ? (
        <div className="flex items-center gap-2">
          <code className="flex-1 text-sm font-mono font-bold tabular-nums text-white bg-black/30 rounded-lg px-2.5 py-1.5" data-testid={`v2-wci-roomid-display-${pairing.id}-${legNum}`}>
            {roomId}
          </code>
          <button
            type="button"
            onClick={() => { navigator.clipboard?.writeText(roomId); toast.success("Copied"); }}
            data-testid={`v2-wci-roomid-copy-${pairing.id}-${legNum}`}
            className="rounded-lg bg-[#7C3AED] text-white font-bold text-[10px] uppercase tracking-widest px-3 py-1.5"
          >
            Copy
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[11px] text-[#A5B4FC]">
          <Loader2 className="w-3 h-3 animate-spin" /> Waiting for host to share the room ID…
        </div>
      )}
    </div>
  );
}

function SubmitForm({ pairing, legNum, iAmHome, onChanged }) {
  const [my, setMy] = useState(0);
  const [opp, setOpp] = useState(0);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/wci/submit", {
        pairing_id: pairing.id,
        leg: legNum,
        my_score: Number(my),
        opp_score: Number(opp),
      });
      toast.success("Submitted.");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-end gap-2">
      <NumField label={`You (${iAmHome ? "home" : "away"})`} v={my}  setV={setMy}  testId={`v2-wci-myscore-${pairing.id}-${legNum}`} />
      <NumField label="Opponent"                              v={opp} setV={setOpp} testId={`v2-wci-oppscore-${pairing.id}-${legNum}`} />
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        data-testid={`v2-wci-submit-${pairing.id}-${legNum}`}
        className="rounded-xl bg-gradient-to-r from-[#FFD700] to-[#FFB000] text-[#0F172A] font-bold text-xs px-4 py-2 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Submit"}
      </button>
    </div>
  );
}

function NumField({ label, v, setV, testId }) {
  return (
    <div className="flex-1">
      <label className="block text-[9px] font-mono uppercase tracking-widest text-white/40">{label}</label>
      <input
        type="number" min="0" max="99" value={v}
        onChange={(e) => setV(e.target.value)}
        data-testid={testId}
        style={{ fontSize: "16px" }}
        className="w-full text-center font-bold tabular-nums bg-black/30 border border-white/10 rounded-xl px-2 py-1.5 text-white"
      />
    </div>
  );
}

/* ===================== LEADERBOARD ===================== */
function LeaderboardView({ pool, userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/wci/leaderboard?pool=${pool}`);
        if (!cancelled) setData(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [pool]);
  if (!data) return <div className="text-center text-xs text-[#71717A] py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>;
  const myRow = data.rows.find((r) => r.user_id === userId);
  const pointsToQualify = myRow && !myRow.eliminated
    ? Math.max(0, (data.qualify_threshold || 0) - (myRow.points || 0))
    : null;

  return (
    <div className="space-y-3" data-testid="v2-wci-leaderboard">
      {myRow && (
        <div className="rounded-2xl bg-[#FFD700]/10 border border-[#FFD700]/40 p-3" data-testid="v2-wci-my-card">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">// You</div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-2xl font-extrabold tabular-nums">#{myRow.rank}</span>
            <span className="text-2xl font-extrabold tabular-nums text-[#FFD700]">{myRow.points} pts</span>
          </div>
          {pointsToQualify !== null && (
            <div className="text-[11px] text-white/70 mt-1" data-testid="v2-wci-points-to-qualify">
              {pointsToQualify > 0
                ? `${pointsToQualify} pts to qualify (top ${data.qualify_count})`
                : "You're currently in the qualifying half."}
            </div>
          )}
        </div>
      )}
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] divide-y divide-white/5">
        {data.rows.map((r) => (
          <div key={r.user_id} className={`flex items-center justify-between px-3 py-2 ${r.me ? "bg-[#FFD700]/5" : ""} ${r.eliminated ? "opacity-50" : ""}`} data-testid={`v2-wci-row-${r.user_id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] font-mono w-6 tabular-nums text-[#71717A]">{r.rank}</span>
              <span className="text-sm font-bold text-white truncate">@{r.username || r.user_id.slice(0,6)}</span>
              {r.eliminated && <span className="text-[8px] font-mono uppercase tracking-widest text-rose-400">out</span>}
            </div>
            <span className="text-sm font-bold tabular-nums text-white">{r.points}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===================== BRACKET ===================== */
function BracketView({ pool }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/wci/bracket?pool=${pool}`);
        if (!cancelled) setData(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [pool]);
  if (!data) return <div className="text-center text-xs text-[#71717A] py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>;
  if (!data.rounds || Object.keys(data.rounds).length === 0) {
    return (
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-sm text-[#A1A1AA]" data-testid="v2-wci-bracket-empty">
        <Crown className="w-6 h-6 mx-auto mb-2 opacity-50" />
        Knockout bracket appears once the real WC group stage finishes. Climb the leaderboard — top 50% advance.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="v2-wci-bracket">
      {Object.entries(data.rounds).map(([round, pairs]) => (
        <div key={round} className="rounded-2xl bg-[#12121C] border border-[#1F1F2A]">
          <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#1F1F2A]">{round}</div>
          {pairs.length === 0 && <div className="px-3 py-2 text-[11px] italic text-[#71717A]">— TBD —</div>}
          {pairs.map((p) => (
            <div key={p.id} className="px-3 py-1.5 text-xs flex items-center justify-between border-t border-white/5">
              <span>{p.home_team?.flag} @{p.home_username || p.home_user?.slice(0,6)}</span>
              <span className="text-white/40">vs</span>
              <span>{p.away_team?.flag} @{p.away_username || p.away_user?.slice(0,6)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


/* ===================== CALENDAR ===================== */
function CalendarView({ pool }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/wci/calendar?pool=${pool}`);
        if (!cancelled) setData(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [pool]);
  if (!data) return <div className="text-center text-xs text-[#71717A] py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>;
  const groupMDs = (data.matchdays || []).filter((m) => m.stage === "group");
  return (
    <div className="space-y-2.5" data-testid="v2-wci-calendar">
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3" data-testid="v2-wci-calendar-summary">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] mb-0.5">// Group Stage</div>
        <div className="text-base font-extrabold text-white">{groupMDs.length} matchdays · {groupMDs.reduce((n, m) => n + m.fixtures.length, 0)} fixtures</div>
        <div className="text-[11px] text-[#A1A1AA] mt-0.5">Anchored from {groupMDs[0]?.matchday_id || "—"} · 3 fixtures / day · finish on {groupMDs.at(-1)?.matchday_id || "—"}</div>
      </div>
      {groupMDs.map((m) => <MatchdayCard key={m.matchday_id} md={m} />)}
    </div>
  );
}

function MatchdayCard({ md }) {
  const today = md.is_today;
  const past = md.is_past;
  const dateLabel = formatLongDate(md.matchday_id);
  return (
    <div
      className={`rounded-2xl border overflow-hidden ${
        today ? "bg-[#FFD700]/10 border-[#FFD700]/60 shadow-[0_0_40px_-12px_rgba(255,215,0,0.5)]" :
        past  ? "bg-[#0c0c14] border-[#1F1F2A] opacity-60" :
                "bg-[#12121C] border-[#1F1F2A]"
      }`}
      data-testid={`v2-wci-cal-md-${md.matchday_id}`}
    >
      <div className="px-3 py-2 flex items-center justify-between border-b border-white/5">
        <div>
          <div className={`text-xs font-extrabold ${today ? "text-[#FFD700]" : "text-white"}`}>{dateLabel}</div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A]">{md.matchday_id}</div>
        </div>
        {today && (
          <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFD700] text-[#0F172A] font-bold">
            Today · Live
          </span>
        )}
        {!today && !past && (
          <span className="text-[9px] font-mono uppercase tracking-widest text-[#A1A1AA]">
            {md.fixtures.length} games
          </span>
        )}
      </div>
      <div className="divide-y divide-white/5">
        {md.fixtures.map((f) => (
          <div key={f.fixture_code} className="px-3 py-2.5 flex items-center gap-2" data-testid={`v2-wci-cal-fx-${f.fixture_code}`}>
            <span className={`text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0 ${
              today ? "bg-[#FFD700]/20 text-[#FFD700]" : "bg-white/10 text-[#A5B4FC]"
            }`}>
              {f.group ? `G${f.group}` : (md.stage || "—")}
            </span>
            <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-xl shrink-0">{f.home.flag}</span>
                <span className="text-[11px] font-bold text-white truncate">{f.home.name}</span>
              </div>
              <span className="text-[9px] font-mono text-[#71717A] shrink-0">vs</span>
              <div className="flex items-center gap-1.5 min-w-0 justify-end">
                <span className="text-[11px] font-bold text-white truncate text-right">{f.away.name}</span>
                <span className="text-xl shrink-0">{f.away.flag}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatLongDate(iso) {
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
}

/* ===================== GROUPS ===================== */
function GroupsView({ pool }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/wci/groups?pool=${pool}`);
        if (!cancelled) setData(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [pool]);
  if (!data) return <div className="text-center text-xs text-[#71717A] py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="v2-wci-groups">
      {data.groups.map((g) => (
        <div key={g.id} className="rounded-2xl bg-gradient-to-br from-[#12121C] to-[#0a0a14] border border-[#1F1F2A] overflow-hidden" data-testid={`v2-wci-group-${g.id}`}>
          <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2 bg-gradient-to-r from-[#FFD700]/10 to-transparent">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">Group</span>
            <span className="text-lg font-extrabold text-[#FFD700]">{g.id}</span>
          </div>
          <div className="divide-y divide-white/5">
            {g.teams.map((tm) => (
              <div key={tm.code} className="px-3 py-2 flex items-center gap-2">
                <span className="text-xl shrink-0">{tm.flag}</span>
                <span className="text-[12px] font-bold text-white truncate">{tm.name}</span>
                <span className="ml-auto text-[9px] font-mono tracking-widest text-[#52525B]">{tm.code}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
